import React, { useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg';
import { ReactComponent as Save } from '../../assets/svg/Save.svg';
import '../../styles/modals/StudentInfoModal.css';
import Loading from '../Loading';
import { notify } from '../../utils/util';
import { getCourses, getSolutionTopics, getQuickTipsStudent, connectStruggleToContent, getSolutions } from '../../server/server';

interface AddContentModalProps {
	modalIsOpen: boolean;
	closeModal: () => void;
	onSuccess: () => void;
	recommendationId: string;
	contentType: 'solutions' | 'courses' | 'quickTips' | 'journallingPrompts';
	existingIds?: string[];
}

interface MatchResult {
	input: string;
	matched: boolean;
	matchedItem?: any;
	reason?: string;
}

function AddContentModal({ modalIsOpen, closeModal, onSuccess, recommendationId, contentType, existingIds = [] }: AddContentModalProps) {
	const [processing, setProcessing] = useState(false);
	const [items, setItems] = useState<any[]>([]);
	const [loadingData, setLoadingData] = useState(false);
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [bulkPasteText, setBulkPasteText] = useState('');
	const [journallingPromptsText, setJournallingPromptsText] = useState('');
	const [showBulkPaste, setShowBulkPaste] = useState(false);
	const [matchResults, setMatchResults] = useState<MatchResult[]>([]);

	const isJournallingPrompt = contentType === 'journallingPrompts';

	useEffect(() => {
		ReactModal.setAppElement('#root');
		if (modalIsOpen) {
			// const element = document.getElementById('home');
			// if (element) element.style.filter = 'blur(5px)';
			loadData();
		} else {
		}
	}, [modalIsOpen, contentType]);

	const loadData = async () => {
		setLoadingData(true);
		try {
			let resp;
			if (contentType === 'courses') {
				resp = await getCourses({});
				if (resp.status === 200) {
					setItems(resp.data?.items || []);
				}
			} else if (contentType === 'solutions') {
				resp = await getSolutions({ limit: 2000, offset: 0 });
				if (resp.status === 200) {
					setItems(resp.data?.items || []);
				}
			} else if (contentType === 'quickTips') {
				resp = await getQuickTipsStudent({ limit: 2000, offset: 0 });
				if (resp.status === 200) {
					setItems(resp.data?.data || []);
				}
			}
		} catch (error) {
			console.error('Error loading data:', error);
			notify('Failed to load data');
		} finally {
			setLoadingData(false);
		}
	};

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!isJournallingPrompt && selectedIds.length === 0) {
			notify('Please select at least one item');
			return;
		}

		setProcessing(true);
		let data: any = {};

		if (contentType === 'courses') {
			data.courseIds = selectedIds;
		} else if (contentType === 'solutions') {
			data.solutionIds = selectedIds;
		} else if (contentType === 'quickTips') {
			data.quickTipIds = selectedIds;
		} else if (contentType === 'journallingPrompts') {
			const lines = journallingPromptsText
				.split('\n')
				.map((line) => line.trim())
				.filter((line) => line.length > 0);
			if (lines.length === 0) {
				notify('Please enter at least one journalling prompt');
				setProcessing(false);
				return;
			}
			data.journallingPrompts = lines;
		}

		// Use new connect API endpoint
		const resp = await connectStruggleToContent(recommendationId, data);
		if (resp.status === 201 || resp.status === 200) {
			notify('Content added successfully!');
			cleanUpFunction();
			closeModal();
			onSuccess();
		} else {
			notify('Failed to add content');
		}
		setProcessing(false);
	};

	const cleanUpFunction = () => {
		setSelectedIds([]);
		setSearchTerm('');
		setProcessing(false);
		setBulkPasteText('');
		setShowBulkPaste(false);
		setMatchResults([]);
	};

	const handleClose = () => {
		cleanUpFunction();
		closeModal();
	};

	const toggleSelection = (id: string) => {
		if (selectedIds.includes(id)) {
			setSelectedIds(selectedIds.filter((item) => item !== id));
		} else {
			setSelectedIds([...selectedIds, id]);
		}
	};

	const getTitle = () => {
		if (contentType === 'courses') return 'Add Courses';
		if (contentType === 'solutions') return 'Add Solutions';
		if (contentType === 'quickTips') return 'Add Quick Tips';
		if (contentType === 'journallingPrompts') return 'Add Journalling Prompts';
	};

	const getItemLabel = (item: any) => {
		if (contentType === 'courses') return item.title;
		if (contentType === 'solutions') return item.name || item.title;
		return item.title;
	};

	const getItemSecondaryLabel = (item: any) => {
		if (contentType === 'solutions' && item.topic?.title) {
			return item.topic.title;
		}
		return null;
	};

	const normalizeString = (str: string): string => {
		return str
			.toLowerCase()
			.trim()
			.replace(/[^\w\s]/g, '')
			.replace(/\s+/g, ' ');
	};

	const findMatchingItem = (inputLine: string, itemsList: any[]): any | null => {
		const normalizedInput = normalizeString(inputLine);

		// First try exact match
		for (const item of itemsList) {
			const label = getItemLabel(item);
			if (label && normalizeString(label) === normalizedInput) {
				return item;
			}
		}

		// Then try partial match (contains)
		for (const item of itemsList) {
			const label = getItemLabel(item);
			if (label && normalizeString(label).includes(normalizedInput)) {
				return item;
			}
		}

		// Finally try reverse (input contains field value)
		for (const item of itemsList) {
			const label = getItemLabel(item);
			if (label && normalizedInput.includes(normalizeString(label))) {
				return item;
			}
		}

		return null;
	};

	const handleProcessBulkPaste = () => {
		const lines = bulkPasteText
			.split('\n')
			.map((line) => line.trim())
			.filter((line) => line.length > 0);

		const results: MatchResult[] = lines.map((line) => {
			const matchedItem = findMatchingItem(line, items);
			if (matchedItem) {
				// Check if already exists
				if (existingIds.includes(matchedItem.id)) {
					return {
						input: line,
						matched: false,
						matchedItem: matchedItem,
						reason: 'Already added',
					};
				}
				return {
					input: line,
					matched: true,
					matchedItem: matchedItem,
				};
			} else {
				return {
					input: line,
					matched: false,
					reason: 'No matching item found',
				};
			}
		});

		setMatchResults(results);

		// Auto-select matched items
		const newSelectedIds = results
			.filter((r) => r.matched && r.matchedItem)
			.map((r) => r.matchedItem.id)
			.filter((id) => !selectedIds.includes(id));

		setSelectedIds([...selectedIds, ...newSelectedIds]);
	};

	// Filter items
	const filteredItems = items.filter((item) => {
		// Exclude items that are already added
		if (existingIds.includes(item.id)) return false;

		const label = getItemLabel(item).toLowerCase();
		const searchLower = searchTerm.toLowerCase();
		const secondaryLabel = getItemSecondaryLabel(item);

		return label.includes(searchLower) || (secondaryLabel && secondaryLabel.toLowerCase().includes(searchLower));
	});

	return (
		<ReactModal
			isOpen={modalIsOpen}
			onAfterClose={cleanUpFunction}
			onRequestClose={handleClose}
			style={{
				overlay: {
					zIndex: 10,
					background: '#00000045',
				},
				content: {
					padding: 0,
					paddingTop: 30,
					paddingBottom: 30,
					opacity: 1,
					width: 800,
					margin: 'auto',
					backgroundColor: 'transparent',
					maxHeight: '100vh',
					border: 'none',
					boxShadow: 'none',
					overflow: 'auto',
				},
			}}
			className='scrollbar-display-none'
		>
			<div className='student-info-modal'>
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<div className='close-modal-icon' onClick={handleClose}>
						<CloseModalIcon />
					</div>
				</div>

				<div className='bg-white rounded-xl mx-3 flex-1 flex flex-col gap-5 px-[40px] py-[30px]'>
					<div className='flex flex-row items-center justify-between'>
						<p className='text-[24px] font-bold leading-8 text-darkViolet'>{getTitle()}</p>
						{!isJournallingPrompt && (
							<button type='button' onClick={() => setShowBulkPaste(!showBulkPaste)} className='px-4 py-2 rounded-full border border-primaryViolet bg-white text-[14px] font-semibold text-primaryViolet transition hover:bg-primaryViolet hover:text-white'>
								{showBulkPaste ? 'Hide Bulk Paste' : 'Bulk Paste'}
							</button>
						)}
					</div>

					{loadingData ? (
						<div className='flex h-[400px] items-center justify-center'>
							<Loading size={70} primaryColor='#5E639B' stroke={3} />
						</div>
					) : !isJournallingPrompt ? (
						<form onSubmit={onSubmit} className='flex flex-col gap-6'>
							{showBulkPaste && (
								<div className='flex flex-col gap-3 p-4 bg-gray-50 rounded-lg border border-primaryViolet'>
									<label className='text-[16px] font-semibold text-primaryViolet'>Bulk Paste</label>
									<p className='text-[12px] text-mudPurple'>Paste a list of {contentType} names (one per line) to automatically match and select them:</p>
									<textarea value={bulkPasteText} onChange={(e) => setBulkPasteText(e.target.value)} placeholder={`Example:\nSolution Name 1\nSolution Name 2\nSolution Name 3`} className='w-full h-32 p-3 border-2 border-primaryViolet rounded-lg text-[14px] font-medium text-primaryViolet placeholder:text-mudPurple focus:outline-none focus:border-darkViolet resize-none' />
									<button type='button' onClick={handleProcessBulkPaste} disabled={!bulkPasteText.trim()} className={`px-4 py-2 rounded-full text-[14px] font-semibold transition ${bulkPasteText.trim() ? 'bg-primaryViolet text-white hover:bg-darkViolet' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>
										Process List
									</button>

									{matchResults.length > 0 && (
										<div className='mt-3'>
											<div className='flex flex-row gap-4 mb-3'>
												<div className='flex items-center gap-2'>
													<div className='w-3 h-3 bg-green-500 rounded'></div>
													<span className='text-[12px] font-semibold text-primaryViolet'>Matched: {matchResults.filter((r) => r.matched).length}</span>
												</div>
												<div className='flex items-center gap-2'>
													<div className='w-3 h-3 bg-red-500 rounded'></div>
													<span className='text-[12px] font-semibold text-primaryViolet'>Not Matched: {matchResults.filter((r) => !r.matched).length}</span>
												</div>
											</div>

											<div className='space-y-2 max-h-48 overflow-y-auto'>
												{matchResults.map((result, index) => (
													<div key={index} className={`p-2 rounded-lg border-2 ${result.matched ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
														<div className='flex flex-row justify-between items-start gap-2'>
															<div className='flex-1'>
																<p className='text-[12px] font-semibold text-primaryViolet'>{result.input}</p>
																{result.matched && result.matchedItem ? <p className='text-[11px] text-green-700 mt-1'>✓ Matched: {getItemLabel(result.matchedItem)}</p> : <p className='text-[11px] text-red-700 mt-1'>✗ {result.reason || 'No match found'}</p>}
															</div>
															<div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${result.matched ? 'bg-green-500' : 'bg-red-500'}`}>
																<span className='text-white text-[10px] font-bold'>{result.matched ? '✓' : '✗'}</span>
															</div>
														</div>
													</div>
												))}
											</div>
										</div>
									)}
								</div>
							)}

							<div className='flex flex-col gap-2'>
								<label className='text-[16px] font-semibold text-primaryViolet'>Search</label>
								<input type='text' value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className='rounded-lg border border-mudPurple px-4 py-2 text-[14px] font-medium text-primaryViolet outline-none focus:border-primaryViolet' placeholder={`Search ${contentType}...`} />
							</div>

							<div className='flex flex-col gap-2'>
								<label className='text-[16px] font-semibold text-primaryViolet'>
									Select Items
									<span className='ml-2 text-[14px] font-normal text-mudPurple'>({selectedIds.length} selected)</span>
								</label>
								<div className='max-h-[400px] overflow-y-auto rounded-lg border border-mudPurple p-4'>
									{filteredItems.length === 0 ? (
										<p className='text-mudPurple'>{searchTerm ? `No matching ${contentType} found` : `No ${contentType} available`}</p>
									) : (
										<div className='flex flex-col gap-1'>
											{filteredItems.map((item: any) => {
												const secondaryLabel = getItemSecondaryLabel(item);
												return (
													<label key={item.id} className='flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded'>
														<input type='checkbox' checked={selectedIds.includes(item.id)} onChange={() => toggleSelection(item.id)} className='h-4 w-4' />
														<span className='text-[14px] font-medium text-primaryViolet flex-1'>{getItemLabel(item)}</span>
														{secondaryLabel && <span className='text-[12px] font-normal text-greyMedium'>{secondaryLabel}</span>}
													</label>
												);
											})}
										</div>
									)}
								</div>
							</div>

							<div className='flex flex-row justify-end gap-4 pt-4 '>
								<button type='button' onClick={handleClose} className='px-6 py-3 rounded-full border border-mudPurple bg-white text-[16px] font-semibold text-primaryViolet transition hover:bg-gray-50' disabled={processing}>
									Cancel
								</button>
								<button type='submit' className='flex items-center gap-2 px-6 py-3 rounded-full border border-primaryViolet bg-primaryViolet text-[16px] font-semibold text-white transition hover:bg-opacity-90 disabled:opacity-50' disabled={processing}>
									{processing ? (
										<Loading size={20} primaryColor='#fff' stroke={2} />
									) : (
										<>
											<Save />
											Add
										</>
									)}
								</button>
							</div>
						</form>
					) : (
						<form onSubmit={onSubmit} className='flex flex-col gap-6'>
							<div>
								<p className='text-[12px] text-mudPurple'>Enter list of journalling prompts your want to add:</p>
								<textarea
									value={journallingPromptsText}
									onChange={(e) => setJournallingPromptsText(e.target.value)}
									placeholder={`Example:\nJournalling prompt 1\nJournalling prompt 2\nJournalling prompt 3`}
									className='w-full h-32 p-3 border-2 border-primaryViolet rounded-lg text-[14px] font-medium text-primaryViolet placeholder:text-mudPurple focus:outline-none focus:border-darkViolet resize-none'
								/>
							</div>
							<div className='flex flex-row justify-end gap-4 pt-4 '>
								<button type='button' onClick={handleClose} className='px-6 py-3 rounded-full border border-mudPurple bg-white text-[16px] font-semibold text-primaryViolet transition hover:bg-gray-50' disabled={processing}>
									Cancel
								</button>
								<button type='submit' className='flex items-center gap-2 px-6 py-3 rounded-full border border-primaryViolet bg-primaryViolet text-[16px] font-semibold text-white transition hover:bg-opacity-90 disabled:opacity-50' disabled={processing}>
									{processing ? (
										<Loading size={20} primaryColor='#fff' stroke={2} />
									) : (
										<>
											<Save />
											Add
										</>
									)}
								</button>
							</div>
						</form>
					)}
				</div>
			</div>
		</ReactModal>
	);
}

export default AddContentModal;
